import { DynamicStep } from "@/types/DynamicStep";
import Event from "@/types/Event";
import Sector from "@/types/Sector";

// Steps
import ChooseTicketForm from "@/components/checkout/steps/ChooseTicketForm.vue";
import MappedPurchaseStep from "@/components/checkout/steps/MappedPurchaseStep.vue";
import PaymentForm from "@/components/checkout/steps/PaymentForm.vue";
import ValidationTicketForm from "@/components/checkout/steps/ValidationTicketForm.vue";
import NominalTicket from "@/types/NominalTicket";
import { i18n } from "@/plugins/i18n";
import Reservation from "@/types/Reservation";
import { useUserStore } from "@/stores/user";
import { cpf } from "./cpf";
import { SeatingType } from "@/types/SeatingType";
import EmbeddedPaymentForm from "@/components/checkout/steps/EmbeddedPaymentForm.vue";
import PaymentSummary from "@/components/checkout/steps/PaymentSummary.vue";
import Order from "@/types/Order";

export function steps(event: Event, sectors: Sector[], tickets: NominalTicket[], order: Order | null): DynamicStep[] {
  /**
   * The buyer picks seats one by one into a Cart, General tickets included, on a single step. Events with only General
   * (or Random) sectors choose amounts instead, map or not.
   */
  const hasSelectedSeats = sectors.some(s => s.seating_type === SeatingType.SELECTED);

  return [
    {
      component: MappedPurchaseStep,
      condition: () => hasSelectedSeats,
      props: {
        event: event,
      },
      step: {
        label: "Escolha de Ingressos",
        mobileLabel: "Ingressos",
      },
      ref: "MappedPurchaseStep",
      actions: {
        back: () => false,
        next: () => false,
      },
    },
    {
      component: ChooseTicketForm,
      condition: () => !hasSelectedSeats,
      value: sectors,
      props: {
        event: event,
      },
      step: {
        label: i18n.global.t("checkout.steps.choice.complete"),
        mobileLabel: i18n.global.t("checkout.steps.choice.short"),
      },
      ref: "ChooseTicketForm",
      actions: {
        back: () => false,
        next: () => true,
      },
    },
    {
      component: ValidationTicketForm,
      condition: () => true,
      value: tickets,
      props: {
        sectors: sectors,
      },
      step: {
        label: "Validações e Identificação de Portadores",
        mobileLabel: "Validação e Portadores",
      },
      ref: "ValidationTicketForm",
      actions: {
        back: () => true,
        next: () => true,
      },
    },
    {
      component: PaymentSummary,
      condition: () => true,
      props: {
        sectors: sectors,
      },
      step: {
        label: "Resumo da Compra",
        mobileLabel: "Resumo da Compra",
      },
      ref: "PaymentSummary",
      actions: {
        back: () => true,
        next: () => true,
      },
    },
    {
      component: PaymentForm,
      condition: () => order?.gateway != null ? order.gateway.type === "api" : event.gateway.type === "api",
      props: {
        sectors: sectors,
      },
      step: {
        label: "Dados de Pagamento",
        mobileLabel: "Pagamento",
      },
      ref: "PaymentForm",
      actions: {
        back: () => true,
        next: () => false,
      },
    },
    {
      component: EmbeddedPaymentForm,
      condition: () => order?.gateway != null ? order.gateway.type === "embed" : event.gateway.type === "embed",
      props: {
        sectors: sectors,
        tickets: tickets,
      },
      step: {
        label: "Dados de Pagamento",
        mobileLabel: "Pagamento",
      },
      ref: "EmbeddedPaymentForm",
      actions: {
        back: () => true,
        next: () => false,
      },
    },
  ];
}

export function generateTickets(sectors: Sector[], reservation: Reservation): NominalTicket[] {
  const user = useUserStore().data;
  const types = sectors.map(s => s.sections.map(sec => sec.tickets).flat()).flat();
  const tickets = [] as NominalTicket[];

  let count = 0;

  for (const item of reservation.items) {
    for (let i = 0; i < item.amount; i++) {
      const type = types.find(t => t.id === item.ticket_type_id);
      const sector = sectors.find(s => s.id === item.sector_id);
      const section = sector?.sections.find(sec => sec.id === item.section_id);

      if (type === undefined || sector === undefined || section === undefined) continue;

      tickets.push({
        name: count === 0 ? user.name : null,
        cpf: count === 0 ? (user.document_type === "passport" ? user.document : cpf.format(user.document)) : null,
        document_type: count === 0 ? user.document_type ?? "cpf" : "cpf",
        email: count === 0 ? user.email : undefined,
        cellphone: count === 0 ? user.cellphone : undefined,
        sector: sector,
        section: section,
        type: type,
        preco: (type.current_batch?.price ?? 0).toString(),
        batch_id: type.current_batch?.id ?? null,
        typeId: type.id,
        seat: reservation.seats?.[count],
        additional: type.additional_fields.map(a => ({ field: a, value: "" })),
      });

      count++;
    }
  }

  return tickets;
}
